import React, { useState, useEffect } from "react";
import Modal from "../../components/Modal";
import { Select } from "antd";
import { getServices } from "../../apis/service";
import { createPackage, updatePackage } from "../../apis/packages";

interface Props {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    editableId?: number | null;
    packageData?: any;
    width?: string;
}

const AddPackages: React.FC<Props> = ({
    open,
    width,
    onClose,
    onSuccess,
    editableId,
    packageData,
}) => {
    const [formData, setFormData] = useState({
        name: "",
        serviceIds: [] as number[],
        payoutMode: "percentage",
        payoutValue: 0,
        payoutCap: 0,
    });

    const [services, setServices] = useState<any[]>([]);
    const isEditMode = Boolean(editableId);
    const [serviceSelectValue, setServiceSelectValue] = useState<number | null>(null);
    const [selectedServices, setSelectedServices] = useState<any[]>([]);
    const [errors, setErrors] = useState<{
        name?: string;
        serviceIds?: string;
        assigneePayout?: string;
    }>({});

    useEffect(() => {
        if (open) {
            fetchServices();
            console.log("packageData received:", packageData);
            if (editableId && packageData) {
                const services = (packageData.packageServices ?? [])
                    .map((s: any) => ({
                        id: s.service?.id ?? s.serviceId,
                        name: s.service?.name ?? "",
                        fee: s.fee ?? s.service?.fee ?? 0,
                        quantity: s.quantity ?? 1,
                        isAssigneeRequired: s.isAssigneeRequired ?? false,
                        payoutMode: s.defaultPayoutMode ?? "fixed",
                        payoutValue: s.defaultPayoutValue ?? 0,
                        payoutCap: s.payoutCap ?? 0,
                    }))
                    .sort((a: any, b: any) => (a.name ?? "").localeCompare(b.name ?? ""));

                setFormData({
                    name: packageData.name || "",
                    serviceIds: services.map((s: any) => s.id),
                    payoutMode: "fixed",
                    payoutValue: 0,
                    payoutCap: 0,
                });

                setSelectedServices(services);
            } else {
                setFormData({
                    name: "",
                    serviceIds: [],
                    payoutMode: "fixed",
                    payoutValue: 0,
                    payoutCap: 0,
                });
                setSelectedServices([]);
            }
        }
    }, [open, editableId, packageData]);

    const fetchServices = async () => {
        try {
            const res: any = await getServices({orderBy: "name", orderDirection: "asc", perPage: 1000});
            setServices(res?.data?.data ?? res?.data ?? []);
        } catch (error) {
            console.log(error);
            setServices([]);
        }
    };

    const validateForm = () => {
        const e: typeof errors = {};
        if (!formData.name.trim()) e.name = "* Name is required";
        if (selectedServices.length === 0) e.serviceIds = "* Select at least one service";

        const assigneeYesServices = selectedServices.filter((s) => s.isAssigneeRequired === true);
        for (const s of assigneeYesServices) {
            const val = s.payoutValue;
            const cap = s.payoutCap;
            if (val == null || val === "" || cap == null || cap === "" || Number(val) < 1 || Number(cap) < 1) {
                e.assigneePayout = "* When Assignee Req is Yes, Payout Value and Payout Cap are required and must be at least 1";
                break;
            }
        }

        setErrors(e);
        return Object.keys(e).length === 0;
    };
    const handleSave = async () => {
        if (!validateForm()) return;

        try {
            const payload = {
                name: formData.name,
                // service_ids: selectedServices.map((s) => s.id),
                services: selectedServices.map((s) => ({
                    id: s.id,
                    fee: s.fee ?? 0,
                    quantity: s.quantity ?? 1,
                    isAssigneeRequired: s.isAssigneeRequired,
                    defaultPayoutMode: s.payoutMode,
                    defaultPayoutValue: s.payoutValue,
                    payoutCap: s.payoutCap,
                })),


            };
            console.log("Payload to send:", payload);
            if (isEditMode) {
                await updatePackage(editableId!, payload);
            } else {
                await createPackage(payload);
            }

            onSuccess();
            onClose();
        } catch (err) {
            console.log("Error saving package:", err);
        }
    };

    if (!open) return null;

    return (
        <Modal
            title={isEditMode ? "Update Package" : "Add Package"}
            onClose={onClose}
            width={"max-w-6xl"}
            modalContent={
                <div className="space-y-4">
                    <div className="flex gap-4">
                        {/* Name */}
                        <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-900">Name</label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={(e) =>
                                    setFormData({ ...formData, name: e.target.value })
                                }
                                className="mt-1 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                            />
                            {errors.name && <span className="text-sm text-red-500">{errors.name}</span>}
                        </div>

                    </div>

                    {/* Add Service - single select with search */}
                    <div>
                        <label className="block text-sm font-medium text-gray-900 mb-1">Add Service</label>
                        <Select
                            style={{ width: "100%", maxWidth: 400 }}
                            placeholder="Search and select a service to add"
                            value={serviceSelectValue}
                            onChange={(value) => {
                                const svc = services.find((s: any) => s.id === value);
                                if (svc && !selectedServices.some((s) => s.id === svc.id)) {
                                    const next = [
                                        ...selectedServices,
                                        {
                                            ...svc,
                                            fee: svc.fee ?? 0,
                                            quantity: 1,
                                            isAssigneeRequired: false,
                                        },
                                    ].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
                                    setSelectedServices(next);
                                }
                                setServiceSelectValue(null);
                            }}
                            showSearch
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                            }
                            options={services
                                .filter((s) => !selectedServices.some((sel) => sel.id === s.id))
                                .map((s) => ({ value: s.id, label: s.name }))}
                            allowClear
                        />
                        {errors.serviceIds && <span className="text-sm text-red-500 block mt-1">{errors.serviceIds}</span>}
                        {errors.assigneePayout && <span className="text-sm text-red-500 block mt-1">{errors.assigneePayout}</span>}
                        <div className="flex-1 flex flex-col mt-0 min-w-0 pt-2">
                            <div className="bg-white rounded-lg border border-gray-300 flex flex-col min-w-0">
                                <div className="overflow-y-auto overflow-x-hidden max-h-[45vh] border-b border-gray-200">
                                    <table className="w-full">
                                        <thead className="bg-[#ECEFFF] sticky top-0 z-10">
                                            <tr>
                                                <th className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                                                    Services
                                                </th>
                                                <th className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                                                    Fee
                                                </th>
                                                <th className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                                                    Quantity
                                                </th>
                                                <th className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                                                    Assignee Req
                                                </th>
                                                <th className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                                                    Payout Mode
                                                </th>
                                                <th className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                                                    Payout Value
                                                </th>
                                                <th className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                                                    Payout Cap
                                                </th>
                                                <th className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase w-20">
                                                    Actions
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {selectedServices.length > 0 ? (
                                                selectedServices.map((service, index) => (
                                                    <tr key={service.id || index}>
                                                        <td className="px-4 py-3 text-sm text-gray-900">{service.name}</td>
                                                        <td className="px-4 py-3 text-sm text-gray-900">
                                                            <input
                                                                type="number"
                                                                min={0}
                                                                step="any"
                                                                value={service.fee ?? ""}
                                                                onChange={(e) => {
                                                                    const v = e.target.value === "" ? "" : parseFloat(e.target.value);
                                                                    const updatedServices = selectedServices.map((s) =>
                                                                        s.id === service.id ? { ...s, fee: v } : s
                                                                    );
                                                                    setSelectedServices(updatedServices);
                                                                }}
                                                                className="border rounded px-2 py-1 w-24"
                                                            />
                                                        </td>
                                                        <td className="px-4 py-3 text-sm text-gray-900">
                                                            <input
                                                                type="number"
                                                                min={1}
                                                                value={service.quantity ?? 1}
                                                                onChange={(e) => {
                                                                    const v = e.target.value === "" ? 1 : Math.max(1, parseInt(e.target.value, 10) || 1);
                                                                    const updatedServices = selectedServices.map((s) =>
                                                                        s.id === service.id ? { ...s, quantity: v } : s
                                                                    );
                                                                    setSelectedServices(updatedServices);
                                                                }}
                                                                className="border rounded px-2 py-1 w-20"
                                                            />
                                                        </td>
                                                        <td className="px-4 py-3 text-sm text-gray-900">
                                                            <div className="flex gap-4">
                                                                <label className="flex items-center gap-1 cursor-pointer">
                                                                    <input
                                                                        type="radio"
                                                                        name={`assignee_${service.id}`}
                                                                        checked={service.isAssigneeRequired === true}
                                                                        onChange={() => {
                                                                            const updatedServices = selectedServices.map((s) =>
                                                                                s.id === service.id ? { ...s, isAssigneeRequired: true } : s
                                                                            );
                                                                            setSelectedServices(updatedServices);
                                                                        }}
                                                                    />
                                                                    Yes
                                                                </label>
                                                                <label className="flex items-center gap-1 cursor-pointer">
                                                                    <input
                                                                        type="radio"
                                                                        name={`assignee_${service.id}`}
                                                                        checked={service.isAssigneeRequired !== true}
                                                                        onChange={() => {
                                                                            const updatedServices = selectedServices.map((s) =>
                                                                                s.id === service.id ? { ...s, isAssigneeRequired: false, payoutValue: 0, payoutCap: 0 } : s
                                                                            );
                                                                            setSelectedServices(updatedServices);
                                                                        }}
                                                                    />
                                                                    No
                                                                </label>
                                                            </div>
                                                        </td>

                                                        <td className="px-4 py-3 text-sm text-gray-900">
                                                            {service.isAssigneeRequired ? (
                                                                <div className="flex gap-2">
                                                                    <label className="flex items-center gap-1">
                                                                        <input
                                                                            type="radio"
                                                                            name={`payoutMode_${service.id}`}
                                                                            value="fixed"
                                                                            checked={(service.payoutMode || formData.payoutMode) === "fixed"}
                                                                            onChange={() => {
                                                                                const updatedServices = selectedServices.map((s) =>
                                                                                    s.id === service.id ? { ...s, payoutMode: "fixed" } : s
                                                                                );
                                                                                setSelectedServices(updatedServices);
                                                                            }}
                                                                        />
                                                                        Fixed
                                                                    </label>
                                                                    <label className="flex items-center gap-1">
                                                                        <input
                                                                            type="radio"
                                                                            name={`payoutMode_${service.id}`}
                                                                            value="percentage"
                                                                            checked={(service.payoutMode || formData.payoutMode) === "percentage"}
                                                                            onChange={() => {
                                                                                const updatedServices = selectedServices.map((s) =>
                                                                                    s.id === service.id ? { ...s, payoutMode: "percentage" } : s
                                                                                );
                                                                                setSelectedServices(updatedServices);
                                                                            }}
                                                                        />
                                                                        Percentage
                                                                    </label>
                                                                </div>
                                                            ) : (
                                                                <span className="text-gray-400">—</span>
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm text-gray-900">
                                                            {service.isAssigneeRequired ? (
                                                                service.payoutMode === "percentage" ? (
                                                                    <div className="relative w-24">
                                                                        <input
                                                                            type="number"
                                                                            min={1}
                                                                            value={service.payoutValue ?? ""}
                                                                            onChange={(e) => {
                                                                                const v = e.target.value === "" ? "" : parseFloat(e.target.value);
                                                                                const updatedServices = selectedServices.map((s) =>
                                                                                    s.id === service.id ? { ...s, payoutValue: v } : s
                                                                                );
                                                                                setSelectedServices(updatedServices);
                                                                            }}
                                                                            className="border rounded px-2 py-1 w-full pr-7"
                                                                        />
                                                                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none">
                                                                            %
                                                                        </span>
                                                                    </div>
                                                                ) : (
                                                                    <input
                                                                        type="number"
                                                                        min={1}
                                                                        value={service.payoutValue ?? ""}
                                                                        onChange={(e) => {
                                                                            const v = e.target.value === "" ? "" : parseFloat(e.target.value);
                                                                            const updatedServices = selectedServices.map((s) =>
                                                                                s.id === service.id ? { ...s, payoutValue: v } : s
                                                                            );
                                                                            setSelectedServices(updatedServices);
                                                                        }}
                                                                        className="border rounded px-2 py-1 w-24"
                                                                    />
                                                                )
                                                            ) : (
                                                                <span className="text-gray-400">—</span>
                                                            )}
                                                        </td>

                                                        <td className="px-4 py-3 text-sm text-gray-900">
                                                            {service.isAssigneeRequired ? (
                                                                <input
                                                                    type="number"
                                                                    min={1}
                                                                    value={service.payoutCap ?? ""}
                                                                    onChange={(e) => {
                                                                        const v = e.target.value === "" ? "" : parseFloat(e.target.value);
                                                                        const updatedServices = selectedServices.map((s) =>
                                                                            s.id === service.id ? { ...s, payoutCap: v } : s
                                                                        );
                                                                        setSelectedServices(updatedServices);
                                                                    }}
                                                                    className="border rounded px-2 py-1 w-20"
                                                                />
                                                            ) : (
                                                                <span className="text-gray-400">—</span>
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm text-gray-900">
                                                            <button
                                                                type="button"
                                                                onClick={() => setSelectedServices(selectedServices.filter((s) => s.id !== service.id))}
                                                                className="text-red-600 hover:text-red-800 text-sm font-medium"
                                                            >
                                                                Remove
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td className="px-4 py-4 text-sm text-center text-gray-500" colSpan={8}>
                                                        No services selected
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                                {selectedServices.length > 0 && (
                                    <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex justify-end items-center gap-2">
                                        <span className="text-sm font-medium text-gray-700">Total:</span>
                                        <span className="text-base font-semibold text-gray-900">
                                            {selectedServices
                                                .reduce((sum, s) => sum + (Number(s.fee) || 0) * (Number(s.quantity) || 1), 0)
                                                .toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end gap-2">
                        <button
                            onClick={handleSave}
                            className="px-3 py-2 bg-primary text-white rounded"
                        >
                            {isEditMode ? "Update" : "Save"}
                        </button>

                        <button
                            onClick={onClose}
                            className="px-3 py-2 border rounded"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            }
        />
    );
};

export default AddPackages;